import type { ContractSaleCommissionType, ContractStatus } from '@prisma/client'

export const useContracts = () => {
  const createContract = async (
    contractData: {
      businessId: string
      agentsIds: string[]
      ownersIds: string[]
      buyersIds: string[]
      salePrice: number
      settledTargetDate: Date
    },
  ) => {
    try {
      const response = await $fetch('/api/v1/contracts', {
        method: 'POST',
        body: contractData,
      })
      return { data: response, error: null }
    }
    catch (error) {
      console.error('Error creating contract:', error)
      return { data: null, error }
    }
  }

  const updateContractById = async (
    contractId: string,
    updatedContractData: {
      agentsIds?: string[]
      ownersIds?: string[]
      ownerSolicitorsIds?: string[]
      buyersIds?: string[]
      buyerSolicitorsIds?: string[]
      salePrice?: number
      initialDeposit?: number
      totalDeposit?: number
      settledDate?: Date
      settledTargetDate?: Date
      saleCommission?: number
      saleCommissionType?: ContractSaleCommissionType
      officeCommissionPercentage?: number
      status?: ContractStatus
      attachments?: JSON[]
    },
  ) => {
    try {
      const response = await $fetch(`/api/v1/contracts/${contractId}`, {
        method: 'PUT',
        body: updatedContractData,
      })
      return { data: response, error: null }
    }
    catch (error) {
      console.error(`Error updating contract with id ${contractId}:`, error)
      return { data: null, error }
    }
  }

  const updateContractAgentById = async (
    contractId: string,
    agentId: string,
    updatedAgentData: {
      commissionPercentage: number
    },

  ) => {
    try {
      const response = await $fetch(`/api/v1/contracts/${contractId}/${agentId}`, {
        method: 'PUT',
        body: updatedAgentData,
      })
      return { data: response, error: null }
    }
    catch (error) {
      console.error(`Error updating contract with id ${contractId}:`, error)
      return { data: null, error }
    }
  }

  const getAllContracts = async (options: {
    startedAt?: Date
    endedAt?: Date
    agents?: string[]
    businesses?: string[]
    stages?: string[]
    sortBy?: string
  }) => {
    const params = new URLSearchParams()

    if (options.startedAt) {
      params.append('startedAt', options.startedAt.toISOString())
    }
    if (options.endedAt) {
      params.append('endedAt', options.endedAt.toISOString())
    }
    options.agents?.forEach(agent =>
      params.append('agents', agent),
    )
    options.businesses?.forEach(business =>
      params.append('businesses', business),
    )
    options.stages?.forEach(stage =>
      params.append('stages', stage),
    )
    if (options.sortBy) {
      params.append('sortBy', options.sortBy)
    }

    const res = await $fetch(`/api/v1/contracts?${params.toString()}`, {
      method: 'GET',
    })
    return res
  }

  const getContractById = async (contractId: string) => {
    const res = await $fetch(`/api/v1/contracts/${contractId}`, {
      method: 'GET',
    })
    return res
  }

  const getActiveContractsWithNoLedger = async () => {
    const res = await $fetch(`/api/v1/contracts/get-active-contracts`, {
      method: 'GET',
    })
    return res
  }

  const getContractLedger = async (contractId: string) => {
    const res = await $fetch(`/api/v1/contracts/${contractId}/get-contract-ledger`, {
      method: 'GET',
    })
    return res
  }

  const sortOptions = [
    {
      label: 'Newest Created',
      value: 'createdAt_desc',
    },
    {
      label: 'Oldest Created',
      value: 'createdAt_asc',
    },
    {
      label: 'Latest Settlement Date',
      value: 'settledTargetDate_desc',
    },
    {
      label: 'Earliest Settlement Date',
      value: 'settledTargetDate_asc',
    },
  ]

  // CONTRACT STAGES
  const createContractStage = async (
    name: string,
    order: number,
  ) => {
    const res = await $fetch(`/api/v1/contracts/stages`, {
      method: 'POST',
      body: { name, order },
    })
    return res
  }

  const fetchContractStages = async () => {
    const res = await $fetch(`/api/v1/contracts/stages`, {
      method: 'GET',
    })
    return res
  }

  const updateContractStage = async (
    id: string,
    updates: {
      name?: string
      order?: number
    },
  ) => {
    const res = await $fetch(`/api/v1/contracts/stages/${id}`, {
      method: 'PUT',
      body: updates,
    })
    return res
  }

  const updateContractStageOrder = async (
    stageUpdates: {
      id: string
      name: string
      order: number
    }[],
  ) => {
    const res = await $fetch(`/api/v1/contracts/stages/update-order`, {
      method: 'POST',
      body: stageUpdates,
    })
    return res
  }

  const deleteContractStage = async (id: string) => {
    const res = await $fetch(`/api/v1/contracts/stages/${id}`, {
      method: 'DELETE',
    })
    return res
  }

  // CONTRACT STAGES COMPLETION
  const setContractStageCompleted = async (
    contractId: string,
    stageId: string,
    completed: boolean,
  ) => {
    try {
      const response = await $fetch(`/api/v1/contracts/${contractId}/stage-completed`, {
        method: 'POST',
        body: { stageId, completed },
      })
      return { data: response, error: null }
    }
    catch (error) {
      console.error(`Error updating stage completion for contract ${contractId}:`, error)
      return { data: null, error }
    }
  }

  return {
    createContract,
    updateContractById,
    updateContractAgentById,
    getAllContracts,
    getContractById,
    getActiveContractsWithNoLedger,
    getContractLedger,
    sortOptions,
    createContractStage,
    fetchContractStages,
    updateContractStage,
    updateContractStageOrder,
    setContractStageCompleted,
    deleteContractStage,
  }
}
